/**
 * TemplateSettingsTab – Practice documentation format settings.
 *
 * Sections:
 * - Standard document format (logo + footer)
 * - Pre-licensed clinician document format (supervisor info + footer)
 *
 * Settings are persisted to Medplum via useDocumentationSettings hook.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Paper,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { VariableTextarea } from '../../../../lib/template-variables';
import { useDocumentationSettings } from '../hooks';
import type {
  DocumentationSettings,
  StandardDocumentFormat,
  PreLicensedClinicianFormat,
} from '../types';

export function TemplateSettingsTab(): JSX.Element {
  const { settings, loading, error, saveSettings, refetch } = useDocumentationSettings();

  const [localSettings, setLocalSettings] = useState<DocumentationSettings>(settings);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await saveSettings(localSettings);
      refetch();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [saveSettings, localSettings, refetch]);

  const updateStandardFormat = useCallback((patch: Partial<StandardDocumentFormat>) => {
    setLocalSettings((prev) => ({
      ...prev,
      documentFormat: {
        ...prev.documentFormat,
        standardFormat: { ...prev.documentFormat.standardFormat, ...patch },
      },
    }));
  }, []);

  const updatePreLicensedFormat = useCallback((patch: Partial<PreLicensedClinicianFormat>) => {
    setLocalSettings((prev) => ({
      ...prev,
      documentFormat: {
        ...prev.documentFormat,
        preLicensedFormat: { ...prev.documentFormat.preLicensedFormat, ...patch },
      },
    }));
  }, []);

  if (loading) {
    return (
      <Box py="xl" ta="center">
        <Text size="sm" c="dimmed">Loading settings…</Text>
      </Box>
    );
  }

  return (
    <Stack gap="xl">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert icon={<IconCheck size={16} />} color="green" variant="light">
          Settings saved successfully.
        </Alert>
      )}
      {saveError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {saveError}
        </Alert>
      )}

      <Paper withBorder p="lg">
        <Stack gap="lg">
          <Text fw={600} size="lg">Standard document format</Text>
          <Text size="sm" c="dimmed">
            Applied to notes, template previews, printed documents, and PDFs. Upload your logo in Practice Settings.
          </Text>

          <Group justify="space-between">
            <div>
              <Text size="sm" fw={500}>Include practice logo</Text>
              <Text size="xs" c="dimmed">
                Display your practice logo on documents and previews
              </Text>
            </div>
            <Switch
              color="gold"
              checked={localSettings.documentFormat.standardFormat.includePracticeLogo}
              onChange={(e) => {
                const checked = typeof e === 'boolean' ? e : e.currentTarget.checked;
                updateStandardFormat({ includePracticeLogo: checked });
              }}
            />
          </Group>

          <VariableTextarea
            label="Footer information"
            description="Type @ or use {} to insert variables such as organization name or phone."
            placeholder="Enter footer text (e.g., practice name, address, phone, license numbers)"
            value={localSettings.documentFormat.standardFormat.footerText}
            onChange={(next) => updateStandardFormat({ footerText: next })}
            minRows={3}
          />
        </Stack>
      </Paper>

      <Paper withBorder p="lg">
        <Stack gap="lg">
          <Text fw={600} size="lg">Pre-licensed clinician document format</Text>
          <Text size="sm" c="dimmed">
            Used when a pre-licensed provider with a supervisor creates or signs a note.
          </Text>

          <Checkbox
            label="Add supervisor info to notes and billing documents"
            checked={localSettings.documentFormat.preLicensedFormat.addSupervisorInfo}
            onChange={(e) => {
              const checked = typeof e === 'boolean' ? e : e.currentTarget.checked;
              updatePreLicensedFormat({ addSupervisorInfo: checked });
            }}
          />

          <VariableTextarea
            label="Footer information"
            description="Type @ or use {} to insert supervisor and organization variables."
            placeholder="e.g., Employed & supervised by {{supervisor.name}}, {{supervisor.licenseNumber}}"
            value={localSettings.documentFormat.preLicensedFormat.footerText}
            onChange={(next) => updatePreLicensedFormat({ footerText: next })}
            minRows={2}
          />
        </Stack>
      </Paper>

      <Divider />

      <Group justify="flex-end">
        <Button onClick={handleSave} loading={saving}>
          Save all settings
        </Button>
      </Group>
    </Stack>
  );
}
